import React, { useState, useEffect, useCallback, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import { useSelector } from 'react-redux';
import { 
  Box, 
  Typography, 
  Button, 
  Card, 
  CardContent, 
  Chip,
  Stack,
  useTheme,
  CircularProgress,
  Alert,
  AlertTitle,
  Grid,
  Divider,
  Tooltip
} from '@mui/material';
import { 
  CreditCard as CreditCardIcon,
  Schedule as ScheduleIcon,
  AttachMoney as AttachMoneyIcon,
  Cancel as CancelIcon,
  Pause as PauseIcon,
  SwapHoriz as SwapHorizIcon,
  Check as CheckIcon,
  Star as StarIcon,
  PlayArrow as PlayArrowIcon,
  CardGiftcard as GiftIcon,
  Warning as WarningIcon
} from '@mui/icons-material';
import SweetAlert from 'sweetalert2';
import { API_URL, createAxiosInstance } from '../../../api';

const MyPlanTab = React.memo(({ subscriptionStatus, hasPaid, userId, isActive: tabIsActive }) => {
  const theme = useTheme();
  const navigate = useNavigate();
  
  // State
  const [loading, setLoading] = useState(true);
  const [dataLoaded, setDataLoaded] = useState(false);
  const [billingData, setBillingData] = useState({
    status: '',
    membership: '',
    price: '',
    nextBilling: '',
    daysLeft: '',
    description: '',
    cancelAtPeriodEnd: false
  });
  const [trialStatus, setTrialStatus] = useState(null);
  const [changingPlan, setChangingPlan] = useState(false);

  // Available plans
  const availablePlans = useMemo(() => [
    {
      id: 'monthly',
      name: 'Monthly',
      price: 2.00,
      period: 'month',
      description: 'Flexible monthly subscription',
      features: ['Flexible', 'Cancel anytime'],
      popular: false
    },
    {
      id: 'yearly',
      name: 'Yearly',
      price: 19.99,
      period: 'year',
      description: 'Best value for long-term use',
      features: ['Save 17%', 'Cancel anytime'],
      popular: true,
      savingsHighlight: 'Save 17%'
    }
  ], []);

  // Fetch trial status
  useEffect(() => {
    const fetchTrialStatus = async () => {
      if (!userId || !tabIsActive) return;
      
      try {
        const axiosInstance = createAxiosInstance();
        const response = await axiosInstance.get(`${API_URL}/api/billings/trial/status/${userId}`);
        
        if (response.data?.success && response.data?.hasTrial && !response.data?.isExpired) {
          setTrialStatus({
            onTrial: true,
            trialEndDate: response.data.trialEndDate,
            daysRemaining: response.data.daysRemaining,
            isExpiringSoon: response.data.isExpiringSoon,
            isExpired: response.data.isExpired,
          });
        } else {
          setTrialStatus(null);
        }
      } catch (error) {
        console.error('Error fetching trial status:', error);
        setTrialStatus(null);
      }
    };

    fetchTrialStatus();
  }, [userId, tabIsActive]);

  // Fetch billing data only when tab is active and data hasn't been loaded
  useEffect(() => {
    const fetchBillingData = async () => {
      if (!userId || !tabIsActive || dataLoaded) return;
      
      try {
        setLoading(true);
        const axiosInstance = createAxiosInstance();
        const response = await axiosInstance.get(`${API_URL}/api/billings/status/${userId}`);
        
        const {
          status,
          nextBilling,
          membership,
          price,
          description,
          cancel_at_period_end,
          daysLeft
        } = response.data;

        setBillingData({
          status: status || 'inactive',
          membership: membership || 'N/A',
          price: price || 'N/A',
          nextBilling: nextBilling || 'N/A',
          daysLeft: daysLeft || 'N/A',
          description: description || 'N/A',
          cancelAtPeriodEnd: !!cancel_at_period_end
        });
        
        setDataLoaded(true);
      } catch (error) {
        console.error('Error fetching billing data:', error);
        SweetAlert.fire({
          title: 'Error',
          text: 'Failed to load billing information.',
          icon: 'error',
          confirmButtonText: 'OK'
        });
      } finally {
        setLoading(false);
      }
    };

    fetchBillingData();
  }, [userId, tabIsActive, dataLoaded]);

  // Format billing date
  const formattedBilling = useMemo(() => {
    if (!billingData.nextBilling || billingData.nextBilling === 'N/A') return 'N/A';
    return new Date(billingData.nextBilling * 1000).toLocaleDateString('en-UK', {
      weekday: 'long',
      year: 'numeric',
      month: 'long',
      day: 'numeric'
    });
  }, [billingData.nextBilling]);

  // Get status color and text
  const getStatusInfo = useCallback((status, cancelAtPeriodEnd) => {
    if (cancelAtPeriodEnd) {
      return { color: '#d32f2f', text: 'Cancelled', variant: 'error' };
    }
    
    switch (status) {
      case 'active':
        return { color: '#2e7d32', text: 'Active', variant: 'success' };
      case 'paused':
        return { color: '#ed6c02', text: 'Paused', variant: 'warning' };
      case 'past_due':
        return { color: '#d32f2f', text: 'Past Due', variant: 'error' };
      case 'canceled':
        return { color: '#d32f2f', text: 'Cancelled', variant: 'error' };
      default:
        return { color: '#757575', text: 'Inactive', variant: 'default' };
    }
  }, []);

  // Handle cancel subscription
  const handleCancelSubscription = useCallback(async () => {
    const result = await SweetAlert.fire({
      title: 'Cancel Payment plan',
      text: 'Are you sure you want to cancel your payment plan? This action cannot be undone.',
      icon: 'warning',
      showCancelButton: true,
      confirmButtonColor: '#d32f2f',
      cancelButtonColor: theme.palette.primary.main,
      confirmButtonText: 'Yes, Cancel',
      cancelButtonText: 'Keep Payment Plan'
    });

    if (result.isConfirmed) {
      if (!userId) {
        SweetAlert.fire({
          title: 'Error',
          text: 'User ID is missing. Please try logging in again.',
          icon: 'error',
          confirmButtonText: 'OK'
        });
        return;
      }
      try {
        const axiosInstance = createAxiosInstance();
        await axiosInstance.delete(`${API_URL}/api/billings/cancel/${userId}`);
        
        SweetAlert.fire({
          title: 'Payment plan cancelled',
          text: 'Your subscription has been cancelled. It will remain active until the end of the billing period.',
          icon: 'success',
          confirmButtonText: 'OK'
        });
        
        // Refresh data
        window.location.reload();
      } catch (error) {
        console.error('Error cancelling subscription:', error);
        const errorMessage = error.response?.data?.message || error.message || 'Failed to cancel subscription. Please try again.';
        SweetAlert.fire({
          title: 'Error',
          text: errorMessage,
          icon: 'error',
          confirmButtonText: 'OK'
        });
      }
    }
  }, [userId, theme.palette.primary.main]);

  // Handle uncancel subscription (remove cancel_at_period_end)
  const handleUncancelSubscription = useCallback(async () => {
    const result = await SweetAlert.fire({
      title: 'Uncancel Payment Plan',
      text: 'Your payment plan is set to cancel at the end of this period. Do you want to keep it active and continue billing as normal?',
      icon: 'question',
      showCancelButton: true,
      confirmButtonColor: theme.palette.primary.main,
      cancelButtonColor: '#757575',
      confirmButtonText: 'Yes, Keep Payment Plan',
      cancelButtonText: 'Leave Cancelled'
    });

    if (result.isConfirmed) {
      if (!userId) {
        SweetAlert.fire({
          title: 'Error',
          text: 'User ID is missing. Please try logging in again.',
          icon: 'error',
          confirmButtonText: 'OK'
        });
        return;
      }

      try {
        const axiosInstance = createAxiosInstance();
        await axiosInstance.post(`${API_URL}/api/billings/uncancel/${userId}`);

        SweetAlert.fire({
          title: 'Subscription Uncancelled',
          text: 'Your subscription will stay active and continue billing as normal.',
          icon: 'success',
          confirmButtonText: 'OK'
        });

        // Refresh data
        window.location.reload();
      } catch (error) {
        console.error('Error uncancelling subscription:', error);
        const errorMessage = error.response?.data?.message || error.message || 'Failed to uncancel subscription. Please try again.';
        SweetAlert.fire({
          title: 'Error',
          text: errorMessage,
          icon: 'error',
          confirmButtonText: 'OK'
        });
      }
    }
  }, [userId, theme.palette.primary.main]);

  // Handle resume paused subscription
  const handleResumePausedSubscription = useCallback(async () => {
    const result = await SweetAlert.fire({
      title: 'Resume Payment Plan',
      text: 'Are you sure you want to resume your paused payment plan? It will continue as normal.',
      icon: 'question',
      showCancelButton: true,
      confirmButtonColor: theme.palette.primary.main,
      cancelButtonColor: '#757575',
      confirmButtonText: 'Yes, Resume',
      cancelButtonText: 'Keep Paused'
    });

    if (result.isConfirmed) {
      if (!userId) {
        SweetAlert.fire({
          title: 'Error',
          text: 'User ID is missing. Please try logging in again.',
          icon: 'error',
          confirmButtonText: 'OK'
        });
        return;
      }
      try {
        const axiosInstance = createAxiosInstance();
        await axiosInstance.post(`${API_URL}/api/billings/resumes/${userId}`);
        
        SweetAlert.fire({
          title: 'Payment Plan Resumed',
          text: 'Your payment plan has been resumed and will continue as normal.',
          icon: 'success',
          confirmButtonText: 'OK'
        });
        
        // Refresh data
        window.location.reload();
      } catch (error) {
        console.error('Error resuming paused payment plan:', error);
        const errorMessage = error.response?.data?.message || error.message || 'Failed to resume payment plan. Please try again.';
        SweetAlert.fire({
          title: 'Error',
          text: errorMessage,
          icon: 'error',
          confirmButtonText: 'OK'
        });
      }
    }
  }, [userId, theme.palette.primary.main]);

  // Handle pause subscription
  const handlePauseSubscription = useCallback(async () => {
    const result = await SweetAlert.fire({
      title: 'Pause Payment Plan',
      text: 'Are you sure you want to pause your payment plan? You can resume it anytime.',
      icon: 'question',
      showCancelButton: true,
      confirmButtonColor: theme.palette.primary.main,
      cancelButtonColor: '#757575',
      confirmButtonText: 'Yes, Pause',
      cancelButtonText: 'Keep Active'
    });

    if (result.isConfirmed) {
      try {
        const axiosInstance = createAxiosInstance();
        await axiosInstance.post(`${API_URL}/api/billings/pause/${userId}`);
        
        SweetAlert.fire({
          title: 'Payment Plan Paused',
          text: 'Your payment plan has been paused. You can resume it anytime.',
          icon: 'success',
          confirmButtonText: 'OK'
        });
        
        // Refresh data
        window.location.reload();
      } catch (error) {
        console.error('Error pausing subscription:', error);
        SweetAlert.fire({
          title: 'Error',
          text: 'Failed to pause payment plan. Please try again.',
          icon: 'error',
          confirmButtonText: 'OK'
        });
      }
    }
  }, [userId, theme.palette.primary.main]);

  // Handle change plan
  const handleChangePlan = useCallback(async (planId) => {
    const selectedPlan = availablePlans.find(plan => plan.id === planId);
    if (!selectedPlan) return;

    const isCancelled = billingData.cancelAtPeriodEnd;
    const dialogTitle = isCancelled ? 'Subscribe to Plan' : 'Change Plan';
    const dialogHtml = isCancelled
      ? `<div style="text-align: left;">
          <p><strong>Plan:</strong> ${selectedPlan.name}</p>
          <p><strong>Price:</strong> £${selectedPlan.price}/${selectedPlan.period}</p>
        </div>`
      : `<div style="text-align: left;">
          <p><strong>Current Plan:</strong> ${billingData.membership}</p>
          <p><strong>New Plan:</strong> ${selectedPlan.name}</p>
          <p><strong>Price:</strong> £${selectedPlan.price}/${selectedPlan.period}</p>
        </div>`;

    const result = await SweetAlert.fire({
      title: dialogTitle,
      html: dialogHtml,
      icon: 'question',
      iconColor: "#712d94",
      showCancelButton: true,
      confirmButtonColor: theme.palette.primary.main,
      cancelButtonColor: '#757575',
      confirmButtonText: isCancelled ? 'Yes, Subscribe' : 'Yes, Change Plan',
      cancelButtonText: 'Cancel'
    });

    if (result.isConfirmed) {
      try {
        setChangingPlan(true);
        const axiosInstance = createAxiosInstance();
        // Map planId to planName format expected by backend
        const planNameMap = {
          'monthly': 'Monthly Payment Plan',
          'yearly': 'Yearly Payment Plan'
        };
        await axiosInstance.post(`${API_URL}/api/billings/changeplan/${userId}`, {
          planName: planNameMap[selectedPlan.id] || selectedPlan.name
        });
        
        SweetAlert.fire({
          title: 'Plan Changed',
          text: `Your subscription has been changed to ${selectedPlan.name}.`,
          icon: 'success',
          confirmButtonText: 'OK'
        });
        
        // Refresh data
        window.location.reload();
      } catch (error) {
        console.error('Error changing plan:', error);
        SweetAlert.fire({
          title: 'Error',
          text: 'Failed to change plan. Please try again.',
          icon: 'error',
          confirmButtonText: 'OK'
        });
      } finally {
        setChangingPlan(false);
      }
    }
  }, [availablePlans, billingData.membership, theme.palette.primary.main, userId]);

  // Show loading state for tab-specific data
  if (loading) {
    return (
      <Box sx={{ 
        display: 'flex', 
        justifyContent: 'center', 
        alignItems: 'center', 
        height: '300px' 
      }}>
        <CircularProgress />
      </Box>
    );
  }

  const statusInfo = getStatusInfo(billingData.status, billingData.cancelAtPeriodEnd);
  const isActive = billingData.status === 'active' && !billingData.cancelAtPeriodEnd;
  
  // Determine what to show: Trial takes priority
  const showTrial = trialStatus?.onTrial && !trialStatus?.isExpired;
  const showPaidSubscription = isActive && !showTrial;
  const showNoPlan = !showTrial && !showPaidSubscription;

  // Trial color scheme
  const trialColor = trialStatus?.isExpiringSoon ? '#f39c12' : '#0c5460';
  const trialBgColor = trialStatus?.isExpiringSoon ? '#fff3cd' : '#d1ecf1';
  const trialBorderColor = trialStatus?.isExpiringSoon ? '#ffeaa7' : '#bee5eb';

  return (
    <Box sx={{ p: { xs: 2, sm: 2.5, md: 3 } }}>

      <Grid container spacing={{ xs: 2, sm: 3, md: 6 }}>
        {/* Unified Status Section - Trial takes priority */}
        <Grid item xs={12} md={8}>
          <Box sx={{ pr: { xs: 0, md: 5 } }}>
            <Box sx={{ mb: { xs: 2, sm: 3 } }}>
                <Typography variant="h5" sx={{ 
                fontWeight: 700, 
                color: theme.palette.primary.main,
                mb: 1,
                fontSize: { xs: '1.25rem', sm: '1.35rem', md: '1.5rem' },
                display: 'flex',
                alignItems: 'center',
                gap: 1
                }}>
                {showTrial ? (
                  <GiftIcon sx={{ fontSize: { xs: 24, sm: 28 } }} />
                ) : (
                  <CreditCardIcon sx={{ fontSize: { xs: 24, sm: 28 } }} />
                )}
                {showTrial ? 'Free Trial Active' : showPaidSubscription ? 'Active Subscription' : 'Billing Status'}
                </Typography>
                <Typography variant="body2" color="text.secondary" sx={{ fontSize: { xs: '0.8125rem', sm: '0.875rem' } }}>
                {showTrial 
                  ? 'Your free trial period details' 
                  : showPaidSubscription 
                    ? 'Your active payment plan details'
                    : 'Your current billing status'}
                </Typography>
            </Box>
            
            <>
            {/* Show Trial Card if user is on trial */}
            {showTrial && (
            <Box sx={{ position: 'relative', display: 'flex', flexDirection: { xs: 'column', sm: 'row' }, gap: { xs: 2, sm: 0 } }}>
                <Card sx={{ 
                    border: `2px solid ${trialBorderColor}`,
                    borderRadius: 4,
                    position: 'relative',
                    overflow: 'visible',
                    maxWidth: { xs: '100%', sm: '500px' },
                    minWidth: { xs: '100%', sm: 'auto' },
                    width: { xs: '100%', sm: 'auto' },
                    boxShadow: `0 20px 60px ${trialColor}15, 0 8px 32px rgba(0,0,0,0.08)`,
                    transition: 'all 0.4s cubic-bezier(0.4, 0, 0.2, 1)',
                    background: 'linear-gradient(145deg, #ffffff 0%, #fafbfc 100%)',
                    '&:hover': {
                    transform: 'translateY(-8px) scale(1.02)',
                    boxShadow: `0 32px 80px ${trialColor}20, 0 16px 48px rgba(0,0,0,0.12)`
                    }
                }}>
                    {/* Status Badge */}
                    <Box sx={{
                    position: 'absolute',
                    top: { xs: 16, sm: 20 },
                    left: { xs: 16, sm: 20 },
                    zIndex: 1
                    }}>
                    <Chip 
                        label={trialStatus?.isExpiringSoon ? 'Expiring Soon' : 'Free Trial'}
                        sx={{ 
                        fontWeight: 700,
                        fontSize: { xs: '0.75rem', sm: '0.8rem' },
                        height: { xs: 28, sm: 32 },
                        px: { xs: 1.5, sm: 2 },
                        backgroundColor: trialColor,
                        color: '#fff',
                        boxShadow: `0 4px 12px ${trialColor}30`,
                        '& .MuiChip-label': {
                            px: { xs: 0.75, sm: 1 }
                        }
                        }}
                    />
                    </Box>


                    {/* Header */}
                    <Box sx={{
                    background: `linear-gradient(135deg, ${trialBgColor} 0%, ${trialBgColor}80 50%, ${trialBgColor} 100%)`,
                    p: { xs: 2.5, sm: 3, md: 4 },
                    textAlign: 'center',
                    borderBottom: `1px solid ${trialBorderColor}`,
                    position: 'relative'
                    }}>
                    <Box sx={{
                        width: { xs: 64, sm: 70, md: 80 },
                        height: { xs: 64, sm: 70, md: 80 },
                        borderRadius: '50%',
                        background: `linear-gradient(135deg, ${trialColor}20 0%, ${trialColor}10 100%)`,
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        mx: 'auto',
                        mb: { xs: 2, sm: 2.5, md: 3 },
                        mt: { xs: 3, sm: 3.5, md: 4 },
                        boxShadow: `0 8px 24px ${trialColor}20`
                    }}>
                        <GiftIcon sx={{ 
                        color: trialColor, 
                        fontSize: { xs: 32, sm: 36, md: 40 }
                        }} />
                    </Box>
                    <Typography variant="h3" sx={{ 
                        fontWeight: 800,
                        color: trialColor,
                        mb: 1,
                        fontSize: { xs: '2rem', sm: '2.5rem', md: '3rem' },
                        textShadow: `0 2px 4px ${trialColor}20`
                    }}>
                        FREE
                    </Typography>
                    <Typography variant="h6" sx={{ 
                        color: trialColor,
                        fontWeight: 600,
                        opacity: 0.9,
                        fontSize: { xs: '1rem', sm: '1.15rem', md: '1.25rem' }
                    }}>
                        3-Month Trial
                    </Typography>
                    </Box>

                    {/* Content */}
                    <CardContent sx={{ p: { xs: 2.5, sm: 3, md: 4 } }}>
                    <Stack spacing={3}>
                        {/* Trial Info */}
                        <Box>
                        <Typography variant="h6" color="text.primary" sx={{ 
                            mb: 2, 
                            fontWeight: 700,
                            display: 'flex',
                            alignItems: 'center',
                            gap: 1
                        }}>
                            <ScheduleIcon sx={{ color: trialColor, fontSize: 20 }} />
                            Trial Details
                        </Typography>
                        <Stack spacing={2}>
                            <Box sx={{ 
                                display: 'flex', 
                                alignItems: 'center', 
                                gap: 2,
                                p: 1.5,
                                borderRadius: 2,
                                backgroundColor: `${trialColor}05`,
                                border: `1px solid ${trialColor}10`,
                            }}>
                                <Box sx={{ 
                                width: 8, 
                                height: 8, 
                                borderRadius: '50%', 
                                backgroundColor: trialColor,
                                boxShadow: `0 2px 8px ${trialColor}40`
                                }} />
                                <Typography variant="body1" sx={{ fontWeight: 500 }}>
                                    <strong>Days Remaining:</strong> {trialStatus.daysRemaining} {trialStatus.daysRemaining === 1 ? 'day' : 'days'}
                                </Typography>
                            </Box>
                            <Box sx={{ 
                                display: 'flex', 
                                alignItems: 'center', 
                                gap: 2,
                                p: 1.5,
                                borderRadius: 2,
                                backgroundColor: `${trialColor}05`,
                                border: `1px solid ${trialColor}10`,
                            }}>
                                <Box sx={{ 
                                width: 8, 
                                height: 8, 
                                borderRadius: '50%', 
                                backgroundColor: trialColor,
                                boxShadow: `0 2px 8px ${trialColor}40`
                                }} />
                                <Typography variant="body1" sx={{ fontWeight: 500 }}>
                                    <strong>Ends:</strong> {new Date(trialStatus.trialEndDate).toLocaleDateString('en-GB', { day: 'numeric', month: 'long', year: 'numeric' })}
                                </Typography>
                            </Box>
                        </Stack>
                        </Box>
                        
                        {/* Warning if expiring soon */}
                        {trialStatus?.isExpiringSoon && (
                        <Alert 
                            severity="warning" 
                            icon={<WarningIcon />}
                            sx={{
                                backgroundColor: '#fff3cd',
                                border: '1px solid #ffeaa7',
                                '& .MuiAlert-icon': {
                                    color: '#f39c12'
                                }
                            }}
                        >
                            <AlertTitle sx={{ fontWeight: 600, color: '#856404' }}>
                                Trial Ending Soon
                            </AlertTitle>
                            Your free trial will end in {trialStatus.daysRemaining} {trialStatus.daysRemaining === 1 ? 'day' : 'days'}. 
                            Subscribe now to keep your profile active.
                        </Alert>
                        )}
                    </Stack>
                    </CardContent>
                    
                    {/* Subscribe Button for Trial */}
                    <Box sx={{ p: { xs: 2, sm: 3 }, pt: 0 }}>
                        <Button
                            variant="contained"
                            fullWidth
                            onClick={() => navigate('/profile?step=subscription')}
                            sx={{
                                backgroundColor: theme.palette.primary.main,
                                color: '#fff',
                                py: 1.5,
                                fontSize: '1rem',
                                fontWeight: 600,
                                borderRadius: 2,
                                textTransform: 'none',
                                boxShadow: `0 4px 12px ${theme.palette.primary.main}30`,
                                '&:hover': {
                                    backgroundColor: theme.palette.primary.dark,
                                    transform: 'translateY(-2px)',
                                    boxShadow: `0 6px 16px ${theme.palette.primary.main}40`
                                }
                            }}
                        >
                            Subscribe Now
                        </Button>
                    </Box>
                </Card>
            </Box>
            )}

            {/* Show Paid Subscription Card if user has paid subscription (and no active trial) */}
            {showPaidSubscription && (
            <Box sx={{ position: 'relative', display: 'flex', flexDirection: { xs: 'column', sm: 'row' }, gap: { xs: 2, sm: 0 } }}>
                <Card sx={{ 
                    border: `2px solid ${statusInfo.color}`,
                    borderRadius: 4,
                    position: 'relative',
                    overflow: 'visible',
                    maxWidth: { xs: '100%', sm: '500px' },
                    minWidth: { xs: '100%', sm: 'auto' },
                    width: { xs: '100%', sm: 'auto' },
                    boxShadow: `0 20px 60px ${statusInfo.color}15, 0 8px 32px rgba(0,0,0,0.08)`,
                    transition: 'all 0.4s cubic-bezier(0.4, 0, 0.2, 1)',
                    background: 'linear-gradient(145deg, #ffffff 0%, #fafbfc 100%)',
                    '&:hover': {
                    transform: 'translateY(-8px) scale(1.02)',
                    boxShadow: `0 32px 80px ${statusInfo.color}20, 0 16px 48px rgba(0,0,0,0.12)`
                    }
                }}>
                    {/* Status Badge */}
                    <Box sx={{
                    position: 'absolute',
                    top: { xs: 16, sm: 20 },
                    left: { xs: 16, sm: 20 },
                    zIndex: 1
                    }}>
                    <Chip 
                        label={statusInfo.text}
                        color={statusInfo.variant}
                        variant="filled"
                        sx={{ 
                        fontWeight: 700,
                        fontSize: { xs: '0.75rem', sm: '0.8rem' },
                        height: { xs: 28, sm: 32 },
                        px: { xs: 1.5, sm: 2 },
                        boxShadow: `0 4px 12px ${statusInfo.color}30`,
                        '& .MuiChip-label': {
                            px: { xs: 0.75, sm: 1 }
                        }
                        }}
                    />
                    </Box>

                    {/* Header */}
                    <Box sx={{
                    background: `linear-gradient(135deg, ${statusInfo.color}12 0%, ${statusInfo.color}06 50%, ${statusInfo.color}12 100%)`,
                    p: { xs: 2.5, sm: 3, md: 4 },
                    textAlign: 'center',
                    borderBottom: `1px solid ${statusInfo.color}20`,
                    position: 'relative'
                    }}>
                    <Box sx={{
                        width: { xs: 64, sm: 70, md: 80 },
                        height: { xs: 64, sm: 70, md: 80 },
                        borderRadius: '50%',
                        background: `linear-gradient(135deg, ${statusInfo.color}20 0%, ${statusInfo.color}10 100%)`,
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        mx: 'auto',
                        mb: { xs: 2, sm: 2.5, md: 3 },
                        mt: { xs: 3, sm: 3.5, md: 4 },
                        boxShadow: `0 8px 24px ${statusInfo.color}20`
                    }}>
                        <CreditCardIcon sx={{ 
                        color: statusInfo.color, 
                        fontSize: { xs: 32, sm: 36, md: 40 }
                        }} />
                    </Box>
                    <Typography variant="h3" sx={{ 
                        fontWeight: 800,
                        color: statusInfo.color,
                        mb: 1,
                        fontSize: { xs: '2rem', sm: '2.5rem', md: '3rem' },
                        textShadow: `0 2px 4px ${statusInfo.color}20`
                    }}>
                        £{billingData.price}
                    </Typography>
                    <Typography variant="h6" sx={{ 
                        color: 'text.secondary',
                        fontWeight: 600,
                        opacity: 0.8,
                        fontSize: { xs: '1rem', sm: '1.15rem', md: '1.25rem' }
                    }}>
                        {billingData.membership || 'Monthly Plan'}
                    </Typography>
                    {(billingData.membership === 'Monthly' || billingData.price === '2' || billingData.price === '2.00') && (
                      <Typography variant="body2" sx={{ 
                        color: 'text.secondary',
                        fontSize: '0.875rem',
                        mt: 0.5,
                        opacity: 0.7,
                      }}>
                        (Roughly 7p a day!)
                      </Typography>
                    )}
                    </Box>

                    {/* Content */}
                    <CardContent sx={{ p: { xs: 2.5, sm: 3, md: 4 } }}>
                    <Stack spacing={3}>
                        {/* Features */}
                        <Box>
                        <Typography variant="h6" color="text.primary" sx={{ 
                            mb: 2, 
                            fontWeight: 700,
                            display: 'flex',
                            alignItems: 'center',
                            gap: 1
                        }}>
                            <StarIcon sx={{ color: statusInfo.color, fontSize: 20 }} />
                            Included Features
                        </Typography>
                        <Stack spacing={2}>
                            {[
                            'Flexible',
                            'Cancel anytime'
                            ].map((feature, index) => (
                            <Box key={index} sx={{ 
                                display: 'flex', 
                                alignItems: 'center', 
                                gap: 2,
                                p: 1.5,
                                borderRadius: 2,
                                backgroundColor: `${statusInfo.color}05`,
                                border: `1px solid ${statusInfo.color}10`,
                                transition: 'all 0.2s ease',
                                '&:hover': {
                                backgroundColor: `${statusInfo.color}08`,
                                transform: 'translateX(4px)'
                                }
                            }}>
                                <Box sx={{ 
                                width: 8, 
                                height: 8, 
                                borderRadius: '50%', 
                                backgroundColor: statusInfo.color,
                                boxShadow: `0 2px 8px ${statusInfo.color}40`
                                }} />
                                <Typography variant="body1" sx={{ fontWeight: 500 }}>
                                {feature}
                                </Typography>
                            </Box>
                            ))}
                        </Stack>
                        </Box>

                        {/* Billing Info */}
                        {isActive && (
                        <Box sx={{
                            background: `linear-gradient(135deg, ${statusInfo.color}08 0%, ${statusInfo.color}04 100%)`,
                            p: 3,
                            borderRadius: 3,
                            border: `2px solid ${statusInfo.color}20`,
                            position: 'relative',
                            overflow: 'hidden',
                            '&::before': {
                            content: '""',
                            position: 'absolute',
                            top: 0,
                            left: 0,
                            width: '4px',
                            height: '100%',
                            background: `linear-gradient(180deg, ${statusInfo.color} 0%, ${statusInfo.color}80 100%)`
                            }
                        }}>
                            <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                            <Box sx={{
                                width: 40,
                                height: 40,
                                borderRadius: '50%',
                                background: `linear-gradient(135deg, ${statusInfo.color}20 0%, ${statusInfo.color}10 100%)`,
                                display: 'flex',
                                alignItems: 'center',
                                justifyContent: 'center'
                            }}>
                                <ScheduleIcon sx={{ color: statusInfo.color, fontSize: 20 }} />
                            </Box>
                            <Box>
                                <Typography variant="body2" color="text.secondary" sx={{ fontWeight: 600, mb: 0.5 }}>
                                Next Billing
                                </Typography>
                                <Typography variant="h6" sx={{ fontWeight: 700, color: statusInfo.color }}>
                                {formattedBilling}
                                </Typography>
                            </Box>
                            </Box>
                        </Box>
                        )}
                    </Stack>

                    {/* Status-specific messages */}
                    {billingData.cancelAtPeriodEnd && (
                        <Alert severity="warning" sx={{ mt: 2 }}>
                        <AlertTitle>Payment plan cancelled</AlertTitle>
                        Your payment plan has been cancelled. It will remain active until the end of the billing period.
                        </Alert>
                    )}

                    {billingData.status === 'paused' && billingData.daysLeft > 0 && (
                        <Alert severity="info" sx={{ mt: 2 }}>
                        <AlertTitle>Subscription Paused</AlertTitle>
                        Your subscription is paused. {billingData.daysLeft} days remaining in the billing cycle.
                        </Alert>
                    )}
                    </CardContent>
                </Card>

                {/* Action Buttons - Next to Card */}
                {/* Show buttons when subscription is active, paused, or set to cancel at period end */}
                {(
                  (billingData.status === 'active' && !billingData.cancelAtPeriodEnd) ||
                  billingData.status === 'paused' ||
                  billingData.cancelAtPeriodEnd
                ) && (
                     <Box sx={{
                         display: { xs: 'flex', sm: 'flex' },
                         flexDirection: { xs: 'row', sm: 'column' },
                         gap: { xs: 1.5, sm: 1 },
                         pl: { xs: 0, sm: 3 },
                         pt: { xs: 0, sm: 1 },
                         justifyContent: { xs: 'center', sm: 'flex-start' }
                     }}>
                        {/* Show Resume button if subscription is paused */}
                        {billingData.status === 'paused' ? (
                             <Tooltip title="Resume Subscription" arrow placement="left">
                                 <Button
                                     variant="contained"
                                     color="success"
                                     onClick={handleResumePausedSubscription}
                                     sx={{
                                     minWidth: 'auto',
                                     width: 40,
                                     height: 40,
                                     borderRadius: '50%',
                                     backgroundColor: '#2e7d32',
                                     boxShadow: '0 4px 12px rgba(46, 125, 50, 0.3)',
                                     '&:hover': {
                                         backgroundColor: '#1b5e20',
                                         transform: 'scale(1.1)',
                                         boxShadow: '0 6px 16px rgba(46, 125, 50, 0.4)'
                                     }
                                     }}
                                 >
                                     <PlayArrowIcon sx={{ fontSize: 20 }} />
                                 </Button>
                             </Tooltip>
                        ) : billingData.cancelAtPeriodEnd ? (
                            /* Show Uncancel button when subscription is set to cancel at period end */
                            <Tooltip title="Uncancel Subscription" arrow placement="left">
                                <Button
                                    variant="contained"
                                    color="success"
                                    onClick={handleUncancelSubscription}
                                    sx={{
                                      minWidth: 'auto',
                                      width: 40,
                                      height: 40,
                                      borderRadius: '50%',
                                      backgroundColor: '#2e7d32',
                                      boxShadow: '0 4px 12px rgba(46, 125, 50, 0.3)',
                                      '&:hover': {
                                          backgroundColor: '#1b5e20',
                                          transform: 'scale(1.1)',
                                          boxShadow: '0 6px 16px rgba(46, 125, 50, 0.4)'
                                      }
                                    }}
                                >
                                    <CheckIcon sx={{ fontSize: 20 }} />
                                </Button>
                            </Tooltip>
                         ) : (
                             /* Show Cancel and Pause buttons for active subscriptions (not canceled) */
                             <>
                                 <Tooltip title="Cancel Subscription" arrow placement="left">
                                     <Button
                                         variant="contained"
                                         color="error"
                                         onClick={handleCancelSubscription}
                                         sx={{
                                         minWidth: 'auto',
                                         width: 40,
                                         height: 40,
                                         borderRadius: '50%',
                                         backgroundColor: '#d32f2f',
                                         boxShadow: '0 4px 12px rgba(211, 47, 47, 0.3)',
                                         '&:hover': {
                                             backgroundColor: '#b71c1c',
                                             transform: 'scale(1.1)',
                                             boxShadow: '0 6px 16px rgba(211, 47, 47, 0.4)'
                                         }
                                         }}
                                     >
                                         <CancelIcon sx={{ fontSize: 20 }} />
                                     </Button>
                                 </Tooltip>
                                 
                                 <Tooltip title="Pause Subscription" arrow placement="left">
                                     <Button
                                         variant="contained"
                                         color="warning"
                                         onClick={handlePauseSubscription}
                                         sx={{
                                         minWidth: 'auto',
                                         width: 40,
                                         height: 40,
                                         borderRadius: '50%',
                                         backgroundColor: '#ed6c02',
                                         boxShadow: '0 4px 12px rgba(237, 108, 2, 0.3)',
                                         '&:hover': {
                                             backgroundColor: '#e65100',
                                             transform: 'scale(1.1)',
                                             boxShadow: '0 6px 16px rgba(237, 108, 2, 0.4)'
                                         }
                                         }}
                                     >
                                         <PauseIcon sx={{ fontSize: 20 }} />
                                     </Button>
                                 </Tooltip>
                             </>
                         )}
                     </Box>
                 )}
            </Box>
            )}

            {/* Show No Active Plan if user has neither trial nor paid subscription */}
            {showNoPlan && (
            <Box sx={{ width: '100%', mt: 3 }}>
                <Card sx={{ 
                    border: '2px solid #e0e0e0',
                    borderRadius: 4,
                    background: 'linear-gradient(145deg, #ffffff 0%, #fafbfc 100%)',
                    boxShadow: '0 8px 32px rgba(0,0,0,0.08)',
                }}>
                    <CardContent sx={{ p: { xs: 3, sm: 4 }, textAlign: 'center' }}>
                        <Box sx={{
                            width: { xs: 64, sm: 80 },
                            height: { xs: 64, sm: 80 },
                            borderRadius: '50%',
                            background: 'linear-gradient(135deg, #e0e0e0 0%, #f5f5f5 100%)',
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'center',
                            mx: 'auto',
                            mb: 3
                        }}>
                            <CreditCardIcon sx={{ 
                                color: '#9e9e9e', 
                                fontSize: { xs: 32, sm: 40 }
                            }} />
                        </Box>
                        <Typography variant="h5" sx={{ 
                            fontWeight: 700, 
                            color: 'text.primary',
                            mb: 1
                        }}>
                            No Active Plan
                        </Typography>
                        <Typography variant="body1" sx={{ 
                            color: 'text.secondary',
                            mb: 3,
                            maxWidth: '500px',
                            mx: 'auto'
                        }}>
                            You don't have an active subscription or trial. Choose a plan below to get started and make your profile visible to students.
                        </Typography>
                        <Button
                            variant="contained"
                            onClick={() => navigate('/profile?step=subscription')}
                            sx={{
                                backgroundColor: theme.palette.primary.main,
                                color: '#fff',
                                px: 4,
                                py: 1.5,
                                fontSize: '1rem',
                                fontWeight: 600,
                                borderRadius: 2,
                                textTransform: 'none',
                                boxShadow: `0 4px 12px ${theme.palette.primary.main}30`,
                                '&:hover': {
                                    backgroundColor: theme.palette.primary.dark,
                                    transform: 'translateY(-2px)',
                                    boxShadow: `0 6px 16px ${theme.palette.primary.main}40`
                                }
                            }}
                        >
                            Choose a Plan
                        </Button>
                    </CardContent>
                </Card>
            </Box>
            )}
            </>
          </Box>
        </Grid>


        {/* Available Plans Section */}
        <Grid item xs={12}>
          <Box sx={{ mb: { xs: 2, sm: 3 } }}>
            <Typography variant="h5" sx={{ 
              fontWeight: 700, 
              color: theme.palette.primary.main,
              mb: 1,
              fontSize: { xs: '1.25rem', sm: '1.35rem', md: '1.5rem' },
              display: 'flex',
              alignItems: 'center',
              gap: 1
            }}>
              <SwapHorizIcon sx={{ fontSize: { xs: 24, sm: 28 } }} />
              Available Plans
            </Typography>
            <Typography variant="body2" color="text.secondary" sx={{ fontSize: { xs: '0.8125rem', sm: '0.875rem' } }}>
              Compare and switch to a different plan
            </Typography>
          </Box>
          
          <Stack direction={{ xs: 'column', md: 'row' }} spacing={{ xs: 2, sm: 3 }} sx={{ flexWrap: 'wrap', gap: { xs: 2, sm: 3 } }}>
            {availablePlans.map((plan) => (
              <Card 
                key={plan.id}
                sx={{ 
                  border: plan.popular ? `3px solid ${theme.palette.primary.main}` : '2px solid #e2e8f0',
                  borderRadius: 4,
                  position: 'relative',
                  overflow: 'hidden',
                  maxWidth: { xs: '100%', md: '500px' },
                  minWidth: { xs: '100%', md: 'auto' },
                  width: { xs: '100%', md: 'auto' },
                  flex: { xs: '1 1 100%', md: 1 },
                  background: plan.popular 
                    ? `linear-gradient(145deg, #ffffff 0%, ${theme.palette.primary.main}02 100%)` 
                    : 'linear-gradient(145deg, #ffffff 0%, #fafbfc 100%)',
                  boxShadow: plan.popular 
                    ? `0 20px 60px ${theme.palette.primary.main}15, 0 8px 32px rgba(0,0,0,0.08)` 
                    : '0 8px 32px rgba(0,0,0,0.06)',
                  transition: 'all 0.4s cubic-bezier(0.4, 0, 0.2, 1)',
                  '&:hover': {
                    transform: 'translateY(-6px) scale(1.02)',
                    boxShadow: plan.popular 
                      ? `0 32px 80px ${theme.palette.primary.main}25, 0 16px 48px rgba(0,0,0,0.12)` 
                      : '0 16px 48px rgba(0,0,0,0.12)'
                  }
                }}
              >
                {/* Popular Badge */}
                {plan.popular && (
                  <Box sx={{
                    position: 'absolute',
                    top: { xs: 16, sm: 20 },
                    right: { xs: 16, sm: 20 },
                    zIndex: 1
                  }}>
                    <Chip 
                      label="POPULAR"
                      color="primary"
                      variant="filled"
                      sx={{ 
                        fontWeight: 700,
                        fontSize: { xs: '0.7rem', sm: '0.75rem' },
                        height: { xs: 24, sm: 28 },
                        px: { xs: 1.5, sm: 2 },
                        boxShadow: `0 4px 12px ${theme.palette.primary.main}30`,
                        '& .MuiChip-label': {
                          px: { xs: 0.75, sm: 1 }
                        }
                      }}
                    />
                  </Box>
                )}

                {/* Savings Highlight - Corner Badge */}
                {plan.savingsHighlight && (
                  <Box sx={{
                    position: 'absolute',
                    top: { xs: 16, sm: 20 },
                    left: { xs: 16, sm: 20 },
                    zIndex: 1
                  }}>
                    <Box sx={{
                      display: 'flex',
                      alignItems: 'center',
                      gap: 1,
                      px: { xs: 1.5, sm: 2 },
                      py: { xs: 0.75, sm: 1 },
                      borderRadius: 3,
                      background: `linear-gradient(135deg, #ffd700 0%, #ffed4e 100%)`,
                      border: '2px solid #ffd700',
                      boxShadow: '0 4px 12px rgba(255, 215, 0, 0.3)',
                      transform: { xs: 'scale(0.85)', sm: 'scale(0.9)' },
                      transition: 'all 0.3s ease',
                      '&:hover': {
                        transform: { xs: 'scale(0.95)', sm: 'scale(1.0)' },
                        boxShadow: '0 6px 16px rgba(255, 215, 0, 0.4)'
                      }
                    }}>
                      <Typography variant="body2" sx={{ 
                        fontWeight: 800,
                        color: '#1a1a1a',
                        fontSize: { xs: '0.75rem', sm: '0.875rem' },
                        textShadow: '0 1px 2px rgba(255, 255, 255, 0.5)'
                      }}>
                        📢 {plan.savingsHighlight}
                      </Typography>
                    </Box>
                  </Box>
                )}

                <CardContent sx={{ p: { xs: 2.5, sm: 3, md: 4 } }}>
                  <Stack spacing={3}>
                    {/* Plan Header */}
                    <Box sx={{ textAlign: 'center', position: 'relative' }}>
                      {plan.popular && (
                        <Box sx={{
                          position: 'absolute',
                          top: -20,
                          left: '50%',
                          transform: 'translateX(-50%)',
                          width: '100%',
                          height: '4px',
                          background: `linear-gradient(90deg, ${theme.palette.primary.main} 0%, ${theme.palette.primary.main}80 100%)`,
                          borderRadius: '0 0 2px 2px'
                        }} />
                      )}
                      <Typography variant="h5" sx={{ 
                        fontWeight: 700, 
                        mb: 1,
                        fontSize: { xs: '1.15rem', sm: '1.25rem', md: '1.5rem' },
                        color: plan.popular ? theme.palette.primary.main : 'text.primary'
                      }}>
                        {plan.name}
                      </Typography>
                      <Typography variant="body1" color="text.secondary" sx={{ 
                        mb: { xs: 2, sm: 3 }, 
                        fontWeight: 500, 
                        textAlign: 'center',
                        fontSize: { xs: '0.875rem', sm: '1rem' }
                      }}>
                        {plan.description}
                      </Typography>
                      <Box sx={{
                        display: 'inline-flex',
                        alignItems: 'baseline',
                        gap: 1,
                        p: { xs: 1.5, sm: 2 },
                        borderRadius: 3,
                        background: plan.popular 
                          ? `linear-gradient(135deg, ${theme.palette.primary.main}10 0%, ${theme.palette.primary.main}05 100%)`
                          : 'linear-gradient(135deg, #f8fafc 0%, #f1f5f9 100%)',
                        border: plan.popular 
                          ? `2px solid ${theme.palette.primary.main}20`
                          : '2px solid #e2e8f0'
                      }}>
                        <Typography variant="h3" sx={{ 
                          fontWeight: 800,
                          fontSize: { xs: '2rem', sm: '2.5rem', md: '3rem' },
                          color: plan.popular ? theme.palette.primary.main : 'text.primary',
                          textShadow: plan.popular ? `0 2px 4px ${theme.palette.primary.main}20` : 'none'
                        }}>
                          £{plan.price}
                        </Typography>
                        <Typography component="span" variant="h6" color="text.secondary" sx={{ 
                          fontWeight: 600,
                          fontSize: { xs: '1rem', sm: '1.15rem', md: '1.25rem' }
                        }}>
                          /{plan.period}
                        </Typography>
                      </Box>
                    </Box>


                    <Divider sx={{ 
                      borderColor: plan.popular ? `${theme.palette.primary.main}20` : '#e2e8f0',
                      borderWidth: 2
                    }} />

                    {/* Features */}
                    <Box>
                      <Typography variant="h6" color="text.primary" sx={{ 
                        mb: 2, 
                        fontWeight: 700,
                        fontSize: { xs: '1rem', sm: '1.15rem', md: '1.25rem' },
                        display: 'flex',
                        alignItems: 'center',
                        gap: 1
                      }}>
                        <StarIcon sx={{ 
                          color: plan.popular ? theme.palette.primary.main : 'text.secondary', 
                          fontSize: { xs: 18, sm: 20 }
                        }} />
                        Features
                      </Typography>
                      <Stack spacing={{ xs: 2, sm: 2.5 }}>
                        {plan.features.map((feature, index) => (
                          <Box key={index} sx={{ 
                            display: 'flex', 
                            alignItems: 'center', 
                            gap: { xs: 1.5, sm: 2 },
                            p: { xs: 1.5, sm: 2 },
                            borderRadius: 3,
                            backgroundColor: plan.popular 
                              ? `${theme.palette.primary.main}05`
                              : '#f8fafc',
                            border: plan.popular 
                              ? `2px solid ${theme.palette.primary.main}15`
                              : '2px solid #e2e8f0',
                            transition: 'all 0.3s ease',
                            '&:hover': {
                              backgroundColor: plan.popular 
                                ? `${theme.palette.primary.main}08`
                                : '#f1f5f9',
                              transform: 'translateX(6px)',
                              boxShadow: plan.popular 
                                ? `0 4px 16px ${theme.palette.primary.main}20`
                                : '0 4px 16px rgba(0,0,0,0.08)'
                            }
                          }}>
                            <CheckIcon sx={{ 
                              color: plan.popular ? theme.palette.primary.main : 'text.secondary',
                              fontSize: { xs: 18, sm: 20 },
                              flexShrink: 0,
                              boxShadow: plan.popular ? `0 2px 8px ${theme.palette.primary.main}30` : 'none'
                            }} />
                            <Typography variant="h6" sx={{ 
                              fontWeight: 600,
                              fontSize: { xs: '0.9rem', sm: '1rem', md: '1.15rem' },
                              color: plan.popular ? theme.palette.primary.main : 'text.primary'
                            }}>
                              {feature}
                            </Typography>
                          </Box>
                        ))}
                      </Stack>
                    </Box>

                    {/* Action Button */}
                    {(() => {
                      // When subscription is cancelled (cancel_at_period_end), treat as no current plan
                      const hasActivePlan = isActive;
                      const isCurrentPlan = hasActivePlan && (billingData.membership === plan.name || (plan.id === 'monthly' && billingData.membership === 'Monthly') || (plan.id === 'yearly' && billingData.membership === 'Yearly'));

                      return (
                        <Button
                          variant={plan.popular ? "contained" : "outlined"}
                          fullWidth
                          disabled={changingPlan || isCurrentPlan}
                          onClick={() => handleChangePlan(plan.id)}
                          sx={{
                            backgroundColor: plan.popular ? theme.palette.primary.main : 'transparent',
                            borderColor: plan.popular ? theme.palette.primary.main : theme.palette.primary.main,
                            color: plan.popular ? 'white' : theme.palette.primary.main,
                            fontWeight: 700,
                            py: { xs: 1.5, sm: 2 },
                            borderRadius: 3,
                            fontSize: { xs: '0.9rem', sm: '1rem' },
                            textTransform: 'none',
                            boxShadow: plan.popular
                              ? `0 8px 24px ${theme.palette.primary.main}30`
                              : '0 4px 12px rgba(0,0,0,0.08)',
                            '&:hover': {
                              backgroundColor: plan.popular ? theme.palette.primary.dark : theme.palette.primary.main,
                              color: 'white',
                              transform: 'translateY(-2px)',
                              boxShadow: plan.popular
                                ? `0 12px 32px ${theme.palette.primary.main}40`
                                : `0 8px 24px ${theme.palette.primary.main}30`
                            },
                            '&:disabled': {
                              backgroundColor: 'grey.100',
                              color: 'grey.400',
                              borderColor: 'grey.300',
                              boxShadow: 'none',
                              transform: 'none'
                            }
                          }}
                        >
                          {changingPlan ? (
                            <CircularProgress size={20} color="inherit" />
                          ) : isCurrentPlan ? (
                            'Current Plan'
                          ) : (
                            'Select Plan'
                          )}
                        </Button>
                      );
                    })()}
                  </Stack>
                </CardContent>
              </Card>
            ))}
          </Stack>
        </Grid>
      </Grid>
    </Box>
  );
});

MyPlanTab.displayName = 'MyPlanTab';

export default MyPlanTab;
